import React, { useState, useMemo } from 'react';

const TAX_BRACKETS = {
  2024: {
    single: [
      { min: 0, max: 11600, rate: 0.10 },
      { min: 11600, max: 47150, rate: 0.12 },
      { min: 47150, max: 100525, rate: 0.22 },
      { min: 100525, max: 191950, rate: 0.24 },
      { min: 191950, max: 243725, rate: 0.32 },
      { min: 243725, max: 609350, rate: 0.35 },
      { min: 609350, max: Infinity, rate: 0.37 }
    ],
    married: [
      { min: 0, max: 23200, rate: 0.10 },
      { min: 23200, max: 94300, rate: 0.12 },
      { min: 94300, max: 201050, rate: 0.22 },
      { min: 201050, max: 383900, rate: 0.24 },
      { min: 383900, max: 487450, rate: 0.32 },
      { min: 487450, max: 731200, rate: 0.35 },
      { min: 731200, max: Infinity, rate: 0.37 }
    ],
    hoh: [
      { min: 0, max: 16550, rate: 0.10 },
      { min: 16550, max: 63100, rate: 0.12 },
      { min: 63100, max: 100500, rate: 0.22 },
      { min: 100500, max: 191950, rate: 0.24 },
      { min: 191950, max: 243700, rate: 0.32 },
      { min: 243700, max: 609350, rate: 0.35 },
      { min: 609350, max: Infinity, rate: 0.37 }
    ]
  },
  2025: {
    single: [
      { min: 0, max: 11925, rate: 0.10 },
      { min: 11925, max: 48475, rate: 0.12 },
      { min: 48475, max: 103350, rate: 0.22 },
      { min: 103350, max: 197300, rate: 0.24 },
      { min: 197300, max: 250525, rate: 0.32 },
      { min: 250525, max: 626350, rate: 0.35 },
      { min: 626350, max: Infinity, rate: 0.37 }
    ],
    married: [
      { min: 0, max: 23850, rate: 0.10 },
      { min: 23850, max: 96950, rate: 0.12 },
      { min: 96950, max: 206700, rate: 0.22 },
      { min: 206700, max: 394600, rate: 0.24 },
      { min: 394600, max: 501050, rate: 0.32 },
      { min: 501050, max: 751600, rate: 0.35 },
      { min: 751600, max: Infinity, rate: 0.37 }
    ],
    hoh: [
      { min: 0, max: 17000, rate: 0.10 },
      { min: 17000, max: 64850, rate: 0.12 },
      { min: 64850, max: 103350, rate: 0.22 },
      { min: 103350, max: 197300, rate: 0.24 },
      { min: 197300, max: 250500, rate: 0.32 },
      { min: 250500, max: 626350, rate: 0.35 },
      { min: 626350, max: Infinity, rate: 0.37 }
    ]
  },
  2026: {
    single: [
      { min: 0, max: 12400, rate: 0.10 },
      { min: 12400, max: 50400, rate: 0.12 },
      { min: 50400, max: 105700, rate: 0.22 },
      { min: 105700, max: 201775, rate: 0.24 },
      { min: 201775, max: 256225, rate: 0.32 },
      { min: 256225, max: 640600, rate: 0.35 },
      { min: 640600, max: Infinity, rate: 0.37 }
    ],
    married: [
      { min: 0, max: 24800, rate: 0.10 },
      { min: 24800, max: 100800, rate: 0.12 },
      { min: 100800, max: 211400, rate: 0.22 },
      { min: 211400, max: 403550, rate: 0.24 },
      { min: 403550, max: 512450, rate: 0.32 },
      { min: 512450, max: 768700, rate: 0.35 },
      { min: 768700, max: Infinity, rate: 0.37 }
    ],
    hoh: [
      { min: 0, max: 17700, rate: 0.10 },
      { min: 17700, max: 67450, rate: 0.12 },
      { min: 67450, max: 105700, rate: 0.22 },
      { min: 105700, max: 201775, rate: 0.24 },
      { min: 201775, max: 256200, rate: 0.32 },
      { min: 256200, max: 640600, rate: 0.35 },
      { min: 640600, max: Infinity, rate: 0.37 }
    ]
  }
};

const STANDARD_DEDUCTIONS = {
  2024: { single: 14600, married: 29200, hoh: 21900 },
  2025: { single: 15750, married: 31500, hoh: 23625 },
  2026: { single: 16100, married: 32200, hoh: 24150 }
};

const BRACKET_COLORS = [
  '#10b981', '#22c55e', '#84cc16', '#eab308', '#f97316', '#ef4444', '#dc2626'
];

export default function TaxRateCalculator() {
  const [taxYear, setTaxYear] = useState('2026');
  const [filingStatus, setFilingStatus] = useState('single');
  const [grossIncome, setGrossIncome] = useState(150000);
  const [useStandardDeduction, setUseStandardDeduction] = useState(true);
  const [itemizedDeductions, setItemizedDeductions] = useState(0);
  const [additionalIncome, setAdditionalIncome] = useState(10000);
  const [proposedDeduction, setProposedDeduction] = useState(10000);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showIdeas, setShowIdeas] = useState(false);
  const [activeIdea, setActiveIdea] = useState(1);

  const theme = isDarkMode ? {
    bg: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    cardBg: 'rgba(30, 41, 59, 0.8)',
    cardBorder: '#334155',
    inputBg: '#1e293b',
    inputBorder: '#334155',
    text: '#f1f5f9',
    textMuted: '#94a3b8',
    textDim: '#64748b',
    accent: '#3b82f6',
    sliderBg: '#334155',
    rowBg: '#1e293b',
    totalRowBg: '#334155',
    insightBg: 'rgba(15, 23, 42, 0.6)'
  } : {
    bg: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f8fafc 100%)',
    cardBg: 'rgba(255, 255, 255, 0.9)',
    cardBorder: '#cbd5e1',
    inputBg: '#ffffff',
    inputBorder: '#cbd5e1',
    text: '#1e293b',
    textMuted: '#475569',
    textDim: '#64748b',
    accent: '#2563eb',
    sliderBg: '#cbd5e1',
    rowBg: '#f1f5f9',
    totalRowBg: '#e2e8f0',
    insightBg: 'rgba(241, 245, 249, 0.8)'
  };

  const brackets = TAX_BRACKETS[taxYear][filingStatus];
  const standardDeduction = STANDARD_DEDUCTIONS[taxYear][filingStatus];

  const calculateTax = (taxableIncome) => {
    let tax = 0;
    let breakdown = [];
    
    for (const bracket of brackets) {
      if (taxableIncome <= bracket.min) break;
      
      const taxableInBracket = Math.min(taxableIncome, bracket.max) - bracket.min;
      const taxInBracket = taxableInBracket * bracket.rate;
      
      if (taxableInBracket > 0) {
        breakdown.push({
          rate: bracket.rate,
          min: bracket.min,
          max: bracket.max,
          taxableAmount: taxableInBracket,
          taxAmount: taxInBracket
        });
        tax += taxInBracket;
      }
    }
    
    return { tax, breakdown };
  };

  const getMarginalRate = (taxableIncome) => {
    for (const bracket of brackets) {
      if (taxableIncome <= bracket.max) {
        return bracket.rate;
      }
    }
    return brackets[brackets.length - 1].rate;
  };

  const analysis = useMemo(() => {
    const deduction = useStandardDeduction ? standardDeduction : itemizedDeductions;
    const taxableIncome = Math.max(0, grossIncome - deduction);
    const { tax, breakdown } = calculateTax(taxableIncome);
    const effectiveRate = taxableIncome > 0 ? tax / grossIncome : 0;
    const marginalRate = getMarginalRate(taxableIncome);

    // Additional income analysis
    const newTaxableWithIncome = taxableIncome + additionalIncome;
    const { tax: newTaxWithIncome } = calculateTax(newTaxableWithIncome);
    const additionalTax = newTaxWithIncome - tax;
    const additionalEffectiveRate = additionalIncome > 0 ? additionalTax / additionalIncome : 0;

    // Deduction analysis
    const newTaxableWithDeduction = Math.max(0, taxableIncome - proposedDeduction);
    const { tax: newTaxWithDeduction } = calculateTax(newTaxableWithDeduction);
    const taxSavings = tax - newTaxWithDeduction;
    const deductionEffectiveRate = proposedDeduction > 0 ? taxSavings / proposedDeduction : 0;

    return {
      deduction,
      taxableIncome,
      tax,
      breakdown,
      effectiveRate,
      marginalRate,
      additionalTax,
      additionalEffectiveRate,
      taxSavings,
      deductionEffectiveRate,
      newTaxableWithIncome,
      newTaxableWithDeduction
    };
  }, [grossIncome, useStandardDeduction, itemizedDeductions, standardDeduction, additionalIncome, proposedDeduction, brackets]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const parseFormattedNumber = (str) => {
    return Number(str.replace(/,/g, '')) || 0;
  };

  const formatPercent = (value) => {
    return (value * 100).toFixed(2) + '%';
  };

  const filingStatusLabels = {
    single: 'Single',
    married: 'Married Filing Jointly',
    hoh: 'Head of Household'
  };

  // Calculate max income for visualization (cap at 37% bracket start + some buffer)
  const maxBracket = brackets[brackets.length - 2];
  const visualMax = Math.max(analysis.taxableIncome * 1.2, maxBracket.max * 0.8);

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.bg,
      padding: '32px 16px',
      fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif"
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        
        input[type="range"] {
          -webkit-appearance: none;
          appearance: none;
          height: 8px;
          border-radius: 4px;
          background: ${theme.sliderBg};
          outline: none;
        }
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
        }
        input[type="range"]::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: none;
        }
        .number-input {
          background: ${theme.inputBg};
          border: 1px solid ${theme.inputBorder};
          color: ${theme.text};
          padding: 10px 14px;
          border-radius: 8px;
          font-size: 16px;
          font-family: 'DM Mono', monospace;
          width: 100%;
          box-sizing: border-box;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .number-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
        }
        .select-input {
          background: ${theme.inputBg};
          border: 1px solid ${theme.inputBorder};
          color: ${theme.text};
          padding: 10px 14px;
          border-radius: 8px;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          width: 100%;
          cursor: pointer;
          transition: border-color 0.2s;
        }
        .select-input:focus {
          outline: none;
          border-color: #3b82f6;
        }
      `}</style>

      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px', position: 'relative' }}>
          <div style={{ position: 'absolute', right: '0', top: '0', display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setShowIdeas(true)}
              style={{
                background: isDarkMode ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' : 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                border: 'none',
                borderRadius: '10px',
                padding: '10px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#1e293b',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s',
                boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
              }}
            >
              💡 Key Ideas
            </button>
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              style={{
                background: theme.cardBg,
                border: `1px solid ${theme.cardBorder}`,
                borderRadius: '10px',
                padding: '10px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: theme.text,
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
            >
              {isDarkMode ? '☀️' : '🌙'} {isDarkMode ? 'Light Mode' : 'Dark Mode'}
            </button>
          </div>
          <h1 style={{
            fontSize: '36px',
            fontWeight: '700',
            color: theme.text,
            margin: '0 0 12px 0',
            letterSpacing: '-0.02em'
          }}>
            Marginal vs. Effective Tax Rate
          </h1>
          <p style={{
            color: theme.textMuted,
            fontSize: '18px',
            margin: 0,
            maxWidth: '600px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            See how your last dollar is taxed and how much deductions really save you
          </p>
        </div>

        {/* Main Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 380px) 1fr', gap: '24px' }}>
          
          {/* Left Column - Inputs */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* Income & Filing Card */}
            <div style={{
              background: theme.cardBg,
              borderRadius: '16px',
              padding: '24px',
              border: `1px solid ${theme.cardBorder}`
            }}>
              <h3 style={{ color: theme.text, margin: '0 0 20px 0', fontSize: '13px', fontWeight: '600' }}>
                Income & Filing Status
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ color: theme.textMuted, fontSize: '12px', fontWeight: '500', display: 'block', marginBottom: '6px' }}>
                    Tax Year
                  </label>
                  <select 
                    className="select-input"
                    value={taxYear}
                    onChange={(e) => setTaxYear(e.target.value)}
                  >
                    <option value="2026">2026</option>
                    <option value="2025">2025</option>
                    <option value="2024">2024</option>
                  </select>
                </div>
                <div>
                  <label style={{ color: theme.textMuted, fontSize: '12px', fontWeight: '500', display: 'block', marginBottom: '6px' }}>
                    Filing Status
                  </label>
                  <select 
                    className="select-input"
                    value={filingStatus}
                    onChange={(e) => setFilingStatus(e.target.value)}
                  >
                    <option value="single">Single</option>
                    <option value="married">Married Joint</option>
                    <option value="hoh">Head of Household</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ color: theme.textMuted, fontSize: '12px', fontWeight: '500', display: 'block', marginBottom: '6px' }}>
                  Gross Income
                </label>
                <input
                  type="text"
                  className="number-input"
                  value={formatNumber(grossIncome)}
                  onChange={(e) => setGrossIncome(parseFormattedNumber(e.target.value))}
                />
                <input
                  type="range"
                  min="0"
                  max="1000000"
                  step="5000"
                  value={grossIncome}
                  onChange={(e) => setGrossIncome(Number(e.target.value))}
                  style={{ width: '100%', marginTop: '8px' }}
                />
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <input
                    type="checkbox"
                    id="standardDed"
                    checked={useStandardDeduction}
                    onChange={(e) => setUseStandardDeduction(e.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: '#3b82f6' }}
                  />
                  <label htmlFor="standardDed" style={{ color: theme.text, fontSize: '11px', cursor: 'pointer' }}>
                    Use Standard Deduction ({formatCurrency(standardDeduction)})
                  </label>
                </div>
                {!useStandardDeduction && (
                  <div>
                    <label style={{ color: theme.textMuted, fontSize: '12px', fontWeight: '500', display: 'block', marginBottom: '6px' }}>
                      Itemized Deductions
                    </label>
                    <input
                      type="text"
                      className="number-input"
                      value={formatNumber(itemizedDeductions)}
                      onChange={(e) => setItemizedDeductions(parseFormattedNumber(e.target.value))}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Rate Display Card */}
            <div style={{
              background: isDarkMode ? 'linear-gradient(135deg, #1e3a5f 0%, #1e293b 100%)' : 'linear-gradient(135deg, #dbeafe 0%, #f1f5f9 100%)',
              borderRadius: '16px',
              padding: '24px',
              border: `1px solid ${theme.cardBorder}`
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: theme.textMuted, fontSize: '12px', fontWeight: '500', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Effective Rate
                  </div>
                  <div style={{
                    fontSize: '36px',
                    fontWeight: '700',
                    color: '#3b82f6',
                    fontFamily: "'DM Mono', monospace"
                  }}>
                    {formatPercent(analysis.effectiveRate)}
                  </div>
                  <div style={{ color: theme.textDim, fontSize: '11px', marginTop: '4px' }}>
                    Average rate on all income
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: theme.textMuted, fontSize: '12px', fontWeight: '500', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Marginal Rate
                  </div>
                  <div style={{
                    fontSize: '36px',
                    fontWeight: '700',
                    color: '#f97316',
                    fontFamily: "'DM Mono', monospace"
                  }}>
                    {formatPercent(analysis.marginalRate)}
                  </div>
                  <div style={{ color: theme.textDim, fontSize: '11px', marginTop: '4px' }}>
                    Rate on your last dollar
                  </div>
                </div>
              </div>

              <div style={{
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: `1px solid ${theme.cardBorder}`,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px'
              }}>
                <div>
                  <div style={{ color: theme.textMuted, fontSize: '11px', marginBottom: '2px' }}>Taxable Income</div>
                  <div style={{ color: theme.text, fontSize: '12px', fontWeight: '600', fontFamily: "'DM Mono', monospace" }}>
                    {formatCurrency(analysis.taxableIncome)}
                  </div>
                </div>
                <div>
                  <div style={{ color: theme.textMuted, fontSize: '11px', marginBottom: '2px' }}>Total Federal Tax</div>
                  <div style={{ color: theme.text, fontSize: '12px', fontWeight: '600', fontFamily: "'DM Mono', monospace" }}>
                    {formatCurrency(analysis.tax)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* Bracket Visualization */}
            <div style={{
              background: theme.cardBg,
              borderRadius: '16px',
              padding: '24px',
              border: `1px solid ${theme.cardBorder}`
            }}>
              <h3 style={{ color: theme.text, margin: '0 0 20px 0', fontSize: '13px', fontWeight: '600' }}>
                Tax Bracket Breakdown
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {brackets.slice(0, -1).map((bracket, index) => {
                  const bracketWidth = bracket.max - bracket.min;
                  const filledAmount = Math.max(0, Math.min(analysis.taxableIncome - bracket.min, bracketWidth));
                  const fillPercent = (filledAmount / bracketWidth) * 100;
                  const isCurrentBracket = analysis.taxableIncome > bracket.min && analysis.taxableIncome <= bracket.max;
                  
                  return (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '50px',
                        color: isCurrentBracket ? BRACKET_COLORS[index] : theme.textDim,
                        fontSize: '13px',
                        fontWeight: isCurrentBracket ? '700' : '500',
                        fontFamily: "'DM Mono', monospace",
                        textAlign: 'right'
                      }}>
                        {(bracket.rate * 100).toFixed(0)}%
                      </div>
                      <div style={{
                        flex: 1,
                        height: '28px',
                        background: theme.rowBg,
                        borderRadius: '6px',
                        overflow: 'hidden',
                        position: 'relative',
                        border: isCurrentBracket ? `2px solid ${BRACKET_COLORS[index]}` : `1px solid ${theme.cardBorder}`
                      }}>
                        <div style={{
                          width: `${fillPercent}%`,
                          height: '100%',
                          background: `linear-gradient(90deg, ${BRACKET_COLORS[index]}dd, ${BRACKET_COLORS[index]})`,
                          transition: 'width 0.3s ease'
                        }} />
                        {isCurrentBracket && (
                          <div style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: theme.text,
                            fontSize: '11px',
                            fontWeight: '600',
                            textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                          }}>
                            ← YOUR BRACKET
                          </div>
                        )}
                      </div>
                      <div style={{
                        width: '140px',
                        color: theme.textDim,
                        fontSize: '11px',
                        fontFamily: "'DM Mono', monospace"
                      }}>
                        {formatCurrency(bracket.min)} - {formatCurrency(bracket.max)}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Tax by Bracket Table */}
              <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: `1px solid ${theme.cardBorder}` }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: theme.textMuted, marginBottom: '12px' }}>
                  Tax Calculated by Bracket
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {analysis.breakdown.map((item, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      background: theme.rowBg,
                      borderRadius: '6px',
                      borderLeft: `3px solid ${BRACKET_COLORS[index]}`
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ color: BRACKET_COLORS[index], fontWeight: '600', fontSize: '13px', fontFamily: "'DM Mono', monospace" }}>
                          {(item.rate * 100).toFixed(0)}%
                        </span>
                        <span style={{ color: theme.textMuted, fontSize: '12px' }}>
                          on {formatCurrency(item.taxableAmount)}
                        </span>
                      </div>
                      <span style={{ color: theme.text, fontWeight: '600', fontSize: '13px', fontFamily: "'DM Mono', monospace" }}>
                        {formatCurrency(item.taxAmount)}
                      </span>
                    </div>
                  ))}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 12px',
                    background: theme.totalRowBg,
                    borderRadius: '6px',
                    marginTop: '4px'
                  }}>
                    <span style={{ color: theme.text, fontWeight: '600', fontSize: '16px' }}>Total Tax</span>
                    <span style={{ color: '#ef4444', fontWeight: '700', fontSize: '18px', fontFamily: "'DM Mono', monospace" }}>
                      {formatCurrency(analysis.tax)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* What-If Analysis */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              
              {/* Additional Income Card */}
              <div style={{
                background: isDarkMode ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(30, 41, 59, 0.8) 100%)' : 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(255, 255, 255, 0.9) 100%)',
                borderRadius: '16px',
                padding: '24px',
                border: '1px solid rgba(239, 68, 68, 0.3)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: 'rgba(239, 68, 68, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px'
                  }}>
                    📈
                  </div>
                  <h3 style={{ color: theme.text, margin: 0, fontSize: '12px', fontWeight: '600' }}>
                    Add Income
                  </h3>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: theme.textMuted, fontSize: '12px', fontWeight: '500', display: 'block', marginBottom: '6px' }}>
                    Additional Income Amount
                  </label>
                  <input
                    type="text"
                    className="number-input"
                    value={formatNumber(additionalIncome)}
                    onChange={(e) => setAdditionalIncome(parseFormattedNumber(e.target.value))}
                  />
                  <input
                    type="range"
                    min="0"
                    max="100000"
                    step="1000"
                    value={additionalIncome}
                    onChange={(e) => setAdditionalIncome(Number(e.target.value))}
                    style={{ width: '100%', marginTop: '8px' }}
                  />
                </div>

                <div style={{
                  background: theme.insightBg,
                  borderRadius: '10px',
                  padding: '16px'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ color: theme.textMuted, fontSize: '11px', marginBottom: '2px' }}>Additional Tax Owed</div>
                    <div style={{ color: '#ef4444', fontSize: '24px', fontWeight: '700', fontFamily: "'DM Mono', monospace" }}>
                      {formatCurrency(analysis.additionalTax)}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: theme.textMuted, fontSize: '11px', marginBottom: '2px' }}>Tax Rate on New Income</div>
                    <div style={{ color: '#ef4444', fontSize: '24px', fontWeight: '700', fontFamily: "'DM Mono', monospace" }}>
                      {formatPercent(analysis.additionalEffectiveRate)}
                    </div>
                  </div>
                  <div style={{
                    marginTop: '12px',
                    padding: '10px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    borderRadius: '6px',
                    borderLeft: '3px solid #ef4444'
                  }}>
                    <div style={{ color: theme.text, fontSize: '12px', lineHeight: '1.5' }}>
                      Your additional {formatCurrency(additionalIncome)} is taxed at your <strong style={{ color: '#ef4444' }}>marginal rate</strong>, not your {formatPercent(analysis.effectiveRate)} effective rate!
                    </div>
                  </div>
                </div>
              </div>

              {/* Deduction Savings Card */}
              <div style={{
                background: isDarkMode ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(30, 41, 59, 0.8) 100%)' : 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(255, 255, 255, 0.9) 100%)',
                borderRadius: '16px',
                padding: '24px',
                border: '1px solid rgba(34, 197, 94, 0.3)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: 'rgba(34, 197, 94, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px'
                  }}>
                    💰
                  </div>
                  <h3 style={{ color: theme.text, margin: 0, fontSize: '12px', fontWeight: '600' }}>
                    Add Deduction
                  </h3>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: theme.textMuted, fontSize: '12px', fontWeight: '500', display: 'block', marginBottom: '6px' }}>
                    Additional Deduction Amount
                  </label>
                  <input
                    type="text"
                    className="number-input"
                    value={formatNumber(proposedDeduction)}
                    onChange={(e) => setProposedDeduction(parseFormattedNumber(e.target.value))}
                  />
                  <input
                    type="range"
                    min="0"
                    max="100000"
                    step="1000"
                    value={proposedDeduction}
                    onChange={(e) => setProposedDeduction(Number(e.target.value))}
                    style={{ width: '100%', marginTop: '8px' }}
                  />
                </div>

                <div style={{
                  background: theme.insightBg,
                  borderRadius: '10px',
                  padding: '16px'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ color: theme.textMuted, fontSize: '11px', marginBottom: '2px' }}>Tax Savings</div>
                    <div style={{ color: '#22c55e', fontSize: '24px', fontWeight: '700', fontFamily: "'DM Mono', monospace" }}>
                      {formatCurrency(analysis.taxSavings)}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: theme.textMuted, fontSize: '11px', marginBottom: '2px' }}>Savings Rate</div>
                    <div style={{ color: '#22c55e', fontSize: '24px', fontWeight: '700', fontFamily: "'DM Mono', monospace" }}>
                      {formatPercent(analysis.deductionEffectiveRate)}
                    </div>
                  </div>
                  <div style={{
                    marginTop: '12px',
                    padding: '10px',
                    background: 'rgba(34, 197, 94, 0.1)',
                    borderRadius: '6px',
                    borderLeft: '3px solid #22c55e'
                  }}>
                    <div style={{ color: theme.text, fontSize: '12px', lineHeight: '1.5' }}>
                      Your {formatCurrency(proposedDeduction)} deduction saves at your <strong style={{ color: '#22c55e' }}>marginal rate</strong>, not your {formatPercent(analysis.effectiveRate)} effective rate!
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Insight Box */}
        <div style={{
          marginTop: '24px',
          background: isDarkMode ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(30, 41, 59, 0.8) 100%)' : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(255, 255, 255, 0.9) 100%)',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid rgba(59, 130, 246, 0.3)'
        }}>
          <h3 style={{ color: theme.text, margin: '0 0 16px 0', fontSize: '13px', fontWeight: '600' }}>
            💡 Key Insight: Why This Matters
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            <div style={{
              background: theme.insightBg,
              borderRadius: '10px',
              padding: '16px'
            }}>
              <div style={{ color: '#ef4444', fontWeight: '600', marginBottom: '8px', fontSize: '11px' }}>
                Adding Income ↗
              </div>
              <div style={{ color: theme.textMuted, fontSize: '13px', lineHeight: '1.6' }}>
                When you earn extra income (bonus, side gig, investment gains), it stacks on top of your existing income 
                and is taxed at your <strong style={{ color: theme.text }}>marginal rate</strong> ({formatPercent(analysis.marginalRate)}), 
                not your effective rate ({formatPercent(analysis.effectiveRate)}).
              </div>
            </div>
            <div style={{
              background: theme.insightBg,
              borderRadius: '10px',
              padding: '16px'
            }}>
              <div style={{ color: '#22c55e', fontWeight: '600', marginBottom: '8px', fontSize: '11px' }}>
                Creating Deductions ↘
              </div>
              <div style={{ color: theme.textMuted, fontSize: '13px', lineHeight: '1.6' }}>
                When you create a deduction (401k contribution, HSA, business expense), it reduces income from the top of your 
                stack, saving you at your <strong style={{ color: theme.text }}>marginal rate</strong> ({formatPercent(analysis.marginalRate)}), 
                not your effective rate.
              </div>
            </div>
            <div style={{
              background: theme.insightBg,
              borderRadius: '10px',
              padding: '16px'
            }}>
              <div style={{ color: '#3b82f6', fontWeight: '600', marginBottom: '8px', fontSize: '11px' }}>
                Planning Implications 🎯
              </div>
              <div style={{ color: theme.textMuted, fontSize: '13px', lineHeight: '1.6' }}>
                This is why tax-deferred contributions and deductions are more powerful at high income levels. A $30,000 401(k) contribution 
                in the {formatPercent(analysis.marginalRate)} bracket saves <strong style={{ color: theme.text }}>{formatCurrency(30000 * analysis.marginalRate)}</strong> in taxes!
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: '24px',
          textAlign: 'center',
          color: theme.textDim,
          fontSize: '12px'
        }}>
          Federal income tax only. Does not include state taxes, FICA, NIIT, or AMT. For educational purposes only.
        </div>
      </div>

      {/* Ideas Modal */}
      {showIdeas && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowIdeas(false)}>
          <div style={{
            background: isDarkMode ? '#1e293b' : '#ffffff',
            borderRadius: '20px',
            padding: '32px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)'
          }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowIdeas(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: theme.textMuted,
                padding: '4px 8px'
              }}
            >
              ✕
            </button>

            <h2 style={{
              color: theme.text,
              fontSize: '28px',
              fontWeight: '700',
              marginBottom: '20px',
              marginTop: '0'
            }}>
              💡 Key Ideas
            </h2>

            {/* Tabs */}
            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '24px',
              borderBottom: `1px solid ${theme.cardBorder}`,
              paddingBottom: '12px'
            }}>
              {[
                { id: 1, label: 'The Core Confusion', color: '#3b82f6' },
                { id: 2, label: 'New Dollars = Marginal', color: '#ef4444' },
                { id: 3, label: 'Deductions = Marginal', color: '#22c55e' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveIdea(tab.id)}
                  style={{
                    background: activeIdea === tab.id ? tab.color : 'transparent',
                    border: `2px solid ${tab.color}`,
                    borderRadius: '8px',
                    padding: '10px 16px',
                    cursor: 'pointer',
                    color: activeIdea === tab.id ? '#ffffff' : tab.color,
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s',
                    flex: 1
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Idea 1 Content */}
            {activeIdea === 1 && (
              <div style={{
                background: isDarkMode ? '#1e293b' : '#ffffff',
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${theme.cardBorder}`,
                borderLeftWidth: '4px',
                borderLeftColor: '#3b82f6'
              }}>
                <h3 style={{ color: '#3b82f6', margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600' }}>
                  Idea 1 — The Core Confusion
                </h3>
                <div style={{ color: theme.text, fontSize: '15px', lineHeight: '1.7' }}>
                  <p style={{ margin: '0 0 12px 0' }}>
                    <strong>Marginal tax rate</strong> = the rate applied to your <em>last dollar</em> earned
                  </p>
                  <p style={{ margin: '0 0 12px 0' }}>
                    <strong>Effective tax rate</strong> = the <em>average rate</em> across all dollars
                  </p>
                  <p style={{ margin: '0 0 16px 0', color: theme.textMuted }}>
                    People feel taxed at their marginal rate but see their effective rate on paper — that disconnect causes bad planning.
                  </p>
                  <div style={{
                    background: isDarkMode ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    fontStyle: 'italic',
                    fontWeight: '500',
                    marginBottom: '20px'
                  }}>
                    "You don't spend, save, or invest at your effective rate — you make decisions at your marginal rate."
                  </div>

                  {/* Example Section */}
                  <div style={{
                    background: isDarkMode ? 'rgba(59, 130, 246, 0.08)' : '#f8fafc',
                    borderRadius: '10px',
                    padding: '16px',
                    marginTop: '16px'
                  }}>
                    <h4 style={{ color: theme.text, margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
                      📊 Example
                    </h4>
                    <p style={{ margin: '0 0 12px 0', color: theme.textMuted }}>
                      Let's say: <strong style={{ color: theme.text }}>Effective rate = 16%</strong> and <strong style={{ color: theme.text }}>Marginal rate = 24%</strong>
                    </p>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                      <div style={{
                        background: isDarkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
                        padding: '12px',
                        borderRadius: '8px'
                      }}>
                        <div style={{ fontWeight: '600', color: '#ef4444', marginBottom: '8px' }}>If you earn $10,000 more:</div>
                        <div style={{ color: theme.textMuted, fontSize: '14px', marginBottom: '4px' }}>
                          You don't keep $8,400 (16% tax)
                        </div>
                        <div style={{ color: theme.text, fontSize: '14px', fontWeight: '500' }}>
                          You keep closer to $7,600 (24% tax)
                        </div>
                      </div>
                      <div style={{
                        background: isDarkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)',
                        padding: '12px',
                        borderRadius: '8px'
                      }}>
                        <div style={{ fontWeight: '600', color: '#22c55e', marginBottom: '8px' }}>If you contribute $10,000 to 401(k):</div>
                        <div style={{ color: theme.textMuted, fontSize: '14px', marginBottom: '4px' }}>
                          You don't save $1,600 in tax
                        </div>
                        <div style={{ color: theme.text, fontSize: '14px', fontWeight: '500' }}>
                          You save $2,400 in tax
                        </div>
                      </div>
                    </div>
                    
                    <div style={{
                      marginTop: '12px',
                      padding: '10px 12px',
                      background: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)',
                      borderRadius: '6px',
                      fontWeight: '600',
                      fontSize: '14px',
                      textAlign: 'center'
                    }}>
                      Same $10,000. Same person. Different outcome — because marginal rate rules the decision.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Idea 2 Content */}
            {activeIdea === 2 && (
              <div style={{
                background: isDarkMode ? '#1e293b' : '#ffffff',
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${theme.cardBorder}`,
                borderLeftWidth: '4px',
                borderLeftColor: '#ef4444'
              }}>
                <h3 style={{ color: '#ef4444', margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600' }}>
                  Idea 2 — Every New Dollar Is Taxed at the Marginal Rate
                </h3>
                <div style={{ color: theme.text, fontSize: '15px', lineHeight: '1.7' }}>
                  <p style={{ margin: '0 0 12px 0', fontWeight: '500' }}>
                    This is huge — and most people miss it.
                  </p>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: '8px',
                    marginBottom: '16px'
                  }}>
                    {['Wages', 'Bonuses', 'Interest from CDs', 'Dividends', 'Capital Gains', 'RMDs', 'Roth Conversions', 'Side Income'].map((item, i) => (
                      <div key={i} style={{
                        background: isDarkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        textAlign: 'center'
                      }}>
                        {item}
                      </div>
                    ))}
                  </div>
                  <div style={{ color: theme.textMuted, marginBottom: '12px' }}>
                    ➡️ All new dollars stack on top of your income<br />
                    ➡️ They are taxed starting at your highest marginal bracket<br />
                    ➡️ They may blend across brackets, but they enter at the top
                  </div>
                  <div style={{
                    background: isDarkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    fontWeight: '600'
                  }}>
                    Effective rate explains the past. Marginal rate controls the future.
                  </div>
                </div>
              </div>
            )}

            {/* Idea 3 Content */}
            {activeIdea === 3 && (
              <div style={{
                background: isDarkMode ? '#1e293b' : '#ffffff',
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${theme.cardBorder}`,
                borderLeftWidth: '4px',
                borderLeftColor: '#22c55e'
              }}>
                <h3 style={{ color: '#22c55e', margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600' }}>
                  Idea 3 — Deductions Are Worth Your Marginal Rate
                </h3>
                <div style={{ color: theme.text, fontSize: '15px', lineHeight: '1.7' }}>
                  <p style={{ margin: '0 0 12px 0', fontWeight: '500' }}>
                    This is where planning gets powerful.
                  </p>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: '8px',
                    marginBottom: '16px'
                  }}>
                    {['401(k) Contributions', 'Mortgage Interest', 'Charitable Giving', 'Business Deductions'].map((item, i) => (
                      <div key={i} style={{
                        background: isDarkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        textAlign: 'center'
                      }}>
                        {item}
                      </div>
                    ))}
                  </div>
                  <div style={{ color: theme.textMuted, marginBottom: '12px' }}>
                    Each dollar deducted:<br />
                    ✓ Comes off the top<br />
                    ✓ Saves taxes at your highest marginal rate
                  </div>
                  <div style={{
                    background: isDarkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    fontWeight: '600'
                  }}>
                    A $10,000 deduction is not worth "10% or 15%." It's worth whatever your top bracket is.
                  </div>

                  {/* Example Section */}
                  <div style={{
                    background: isDarkMode ? 'rgba(34, 197, 94, 0.08)' : '#f8fafc',
                    borderRadius: '10px',
                    padding: '16px',
                    marginTop: '16px'
                  }}>
                    <h4 style={{ color: theme.text, margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
                      📊 Example
                    </h4>
                    <p style={{ margin: '0 0 12px 0', color: theme.textMuted }}>
                      You're in the <strong style={{ color: theme.text }}>24% marginal bracket</strong> with a <strong style={{ color: theme.text }}>16% effective rate</strong>
                    </p>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                      <div style={{
                        background: isDarkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)',
                        padding: '12px',
                        borderRadius: '8px'
                      }}>
                        <div style={{ fontWeight: '600', color: '#22c55e', marginBottom: '8px' }}>$10,000 to 401(k):</div>
                        <div style={{ color: theme.text, fontSize: '14px', fontWeight: '500' }}>
                          Saves $2,400 in taxes
                        </div>
                        <div style={{ color: theme.textMuted, fontSize: '13px', marginTop: '4px' }}>
                          (24% × $10,000)
                        </div>
                      </div>
                      <div style={{
                        background: isDarkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)',
                        padding: '12px',
                        borderRadius: '8px'
                      }}>
                        <div style={{ fontWeight: '600', color: '#22c55e', marginBottom: '8px' }}>$5,000 Charitable Gift:</div>
                        <div style={{ color: theme.text, fontSize: '14px', fontWeight: '500' }}>
                          Saves $1,200 in taxes
                        </div>
                        <div style={{ color: theme.textMuted, fontSize: '13px', marginTop: '4px' }}>
                          (24% × $5,000)
                        </div>
                      </div>
                    </div>
                    
                    <div style={{
                      marginTop: '12px',
                      padding: '10px 12px',
                      background: isDarkMode ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.15)',
                      borderRadius: '6px',
                      fontWeight: '600',
                      fontSize: '14px',
                      textAlign: 'center'
                    }}>
                      Every deduction saves at your marginal rate — the higher your bracket, the more valuable deductions become.
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={() => setShowIdeas(false)}
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                border: 'none',
                borderRadius: '10px',
                padding: '14px',
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                marginTop: '20px'
              }}
            >
              Got It!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}